import * as React from 'react'
// components
import { DrawerModal, LoaderWithOverlay } from 'components/UI'
// constants
import { Modals } from 'constants/modals'
import { CAN_VIEW_OBJECTS_OPTIONS } from 'constants/security'
// context
import { GroupSecurityContext } from 'containers/Security/components/GroupSecurity/layers/API'
// hooks
import { useApolloClient, useModalData } from 'hooks/index'
// graphql
import { RISK_TEMPLATE_QUERY } from 'graphql/risk/queries'
import { SECURITY_PROFILES_QUERY } from 'graphql/securityProfiles/queries'
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
// themes
import { CloseIcon } from 'components/UI/Icons'
import { Header } from 'components/Modals/PlanItems/styles'
// types
import {
  IUser,
  IPosition,
  CanViewType,
  ISecurityProfile,
  IRiskTemplatesResponse,
  ISystemProcessBusinessRule
} from 'types/graphql'
// view components
import GroupSecurityCreateForm from 'components/Modals/GroupSecurityModal/Form'
// utils
import get from 'lodash/get'
import pipe from 'lodash/fp/pipe'
import find from 'lodash/fp/find'
import { isUuid } from 'utils/uuid'
import { getMemberOrPositionLabel } from 'utils/systemProcess'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { getOptionByValue, getValueFromOption } from 'utils/form'

const PAPER_PROPS = {
  style: {
    height: '100%',
    overflow: 'hidden',
    top: 0
  }
}

const GroupSecurityCreateModal: React.FC = () => {
  const { client } = useApolloClient()

  const {
    hideModal,
    modalData: { isOpen, isEdit, groupSecurity }
  } = useModalData({ modalType: Modals.GROUP_SECURITY_MODAL })

  const [isLoading, setIsLoading] = React.useState(false)
  const [initialValues, setInitialValues] = React.useState(null)

  const { companyId, createGroupSecurity, updateGroupSecurity } = React.useContext(GroupSecurityContext)

  const handleFormSubmit = async (data) => {
    const isRiskTemplate = !CAN_VIEW_OBJECTS_OPTIONS.map(({ value }) => value).includes(data.canView.value)

    const input = {
      active: data.active,
      name: data.name,
      businessRules: [
        {
          users: data.users.map(({ user, securityProfile }) => ({
            userOrPositionId: getValueFromOption(user),
            securityProfileId: getValueFromOption(securityProfile)
          })),
          views: [
            {
              canView: isRiskTemplate ? CanViewType.RiskTemplate : getValueFromOption(data.canView),
              ...(isRiskTemplate && { riskTemplateId: getValueFromOption(data.canView) })
            }
          ]
        }
      ]
    }

    isEdit
      ? await updateGroupSecurity(groupSecurity.id, input, data.users, hideModal)
      : await createGroupSecurity(input, data.users, hideModal)
  }

  const getInitialValues = async () => {
    if (!groupSecurity) {
      return {
        active: true,
        users: [{}],
        canView: CAN_VIEW_OBJECTS_OPTIONS[0]
      }
    }

    try {
      setIsLoading(true)

      const ids = groupSecurity.businessRules[0].users
        .map((user: ISystemProcessBusinessRule) => user.userOrPositionId)
        .filter((id) => isUuid(id))

      const usersAndPositions = get(
        await client.query({
          query: USERS_OR_POSITIONS_QUERY,
          variables: { filter: { companyId, ids } }
        }),
        'data.usersOrPositions.data'
      )

      const companySecurityProfiles = get(
        await client.query({
          query: SECURITY_PROFILES_QUERY,
          variables: { filter: { companyId }, pagination: { pageTake: 1000, pageSkip: 0 } }
        }),
        'data.securityProfiles.data'
      )

      const canViewValue = groupSecurity.businessRules[0].views[0]
      const isRiskTemplate = canViewValue.canView === CanViewType.RiskTemplate

      const riskTemplate = isRiskTemplate
        ? get(
            await client.query<{ riskTemplates: IRiskTemplatesResponse }>({
              query: RISK_TEMPLATE_QUERY,
              variables: { filter: { ids: [canViewValue.riskTemplateId] } }
            }),
            'data.riskTemplates.data[0]'
          )
        : null

      return {
        active: groupSecurity.active,
        name: groupSecurity.name,
        users: groupSecurity.businessRules[0].users.map(({ userOrPositionId, securityProfileId }) => {
          let userOrPosition

          if (userOrPositionId === 'ALL') {
            userOrPosition = { label: 'All Users', value: 'ALL' }
          } else {
            userOrPosition = pipe(
              find(({ id }) => id === userOrPositionId),
              (userOrPosition: IUser | IPosition) => {
                if (userOrPosition) {
                  return { label: getMemberOrPositionLabel(userOrPosition), value: userOrPosition.id }
                }
              }
            )(usersAndPositions)
          }

          const securityProfile = pipe(
            find(({ id }) => id === securityProfileId),
            (securityProfile: ISecurityProfile) => {
              if (securityProfile) {
                return { label: securityProfile.name, value: securityProfile.id }
              }
            }
          )(companySecurityProfiles)

          return {
            user: userOrPosition,
            securityProfile
          }
        }),
        canView: isRiskTemplate
          ? {
              label: riskTemplate?.name,
              value: riskTemplate?.id
            }
          : getOptionByValue(canViewValue.canView, CAN_VIEW_OBJECTS_OPTIONS)
      }
    } catch (errors) {
      console.log('[GroupSecurityCreateModal.getInitialValues]: ', parseGraphQLErrorMessages(errors))
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getInitialValues().then(setInitialValues)
  }, [isOpen])

  return (
    <DrawerModal width={'600px'} isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>{`${isEdit ? 'Edit' : 'Create'} Group Security`}</span>
        <CloseIcon onClick={hideModal} />
      </Header>

      {isLoading ? (
        <LoaderWithOverlay isLoading={isLoading} />
      ) : (
        <GroupSecurityCreateForm
          isEdit={isEdit}
          hideModal={hideModal}
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          groupSecurity={groupSecurity}
        />
      )}
    </DrawerModal>
  )
}

export default GroupSecurityCreateModal
